import { Instagram, ArrowUp, MapPin, Facebook } from "lucide-react";
import { useNavigate } from "react-router-dom";

export const FooterSection = () => {
  const navigate = useNavigate();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer data-testid="footer-section" className="bg-[#09090b] border-t border-white/5">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-8">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img
                src="https://customer-assets.emergentagent.com/job_auto-paint-brisbane/artifacts/37p3il2l_Screenshot%202025-04-27%20024102.png"
                alt="Velocity Paint and Panel"
                className="h-10 w-auto"
                data-testid="footer-logo"
              />
              <div>
                <span className="font-heading text-xl tracking-[0.2em] uppercase text-white leading-none block">Velocity</span>
                <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-body block">Paint & Panel</span>
              </div>
            </div>
            <p className="text-zinc-600 font-body text-sm mt-4 leading-relaxed max-w-xs">
              Precision automotive refinishing in Woolloongabba, Brisbane.
              JDM specialists. Full resprays. Custom paint.
            </p>
          </div>

          <div>
            <h4 className="font-heading text-sm tracking-[0.3em] uppercase text-zinc-400 mb-6">Quick Links</h4>
            <div className="space-y-3">
              {[
                { label: "Services", href: "#services" },
                { label: "Gallery", href: "#gallery" },
                { label: "About", href: "#about" },
                { label: "Get a Quote", href: "#quote" },
              ].map((link) => (
                <a key={link.label} href={link.href}
                  data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="block text-zinc-600 hover:text-white text-sm font-body tracking-wide transition-colors no-underline"
                  onClick={(e) => { e.preventDefault(); document.querySelector(link.href)?.scrollIntoView({ behavior: "smooth" }); }}
                >
                  {link.label}
                </a>
              ))}
              <button
                onClick={() => navigate("/book")}
                data-testid="footer-link-book"
                className="block text-zinc-600 hover:text-white text-sm font-body tracking-wide transition-colors bg-transparent border-none cursor-pointer p-0"
              >
                Book Appointment
              </button>
            </div>
          </div>

          <div>
            <h4 className="font-heading text-sm tracking-[0.3em] uppercase text-zinc-400 mb-6">Connect</h4>
            <div className="space-y-3 mb-6">
              <a href="sms:0450645926" data-testid="footer-phone-blake" className="block text-zinc-500 hover:text-white text-sm font-mono transition-colors no-underline">
                Blake / 0450 645 926
              </a>
              <a href="sms:0421169339" data-testid="footer-phone-harrison" className="block text-zinc-500 hover:text-white text-sm font-mono transition-colors no-underline">
                Harrisson / 0421 169 339
              </a>
              <a href="mailto:sales@velocitypaintandpanel.com" data-testid="footer-email" className="block text-zinc-500 hover:text-white text-xs font-mono transition-colors no-underline">
                sales@velocitypaintandpanel.com
              </a>
            </div>
            <div className="flex gap-3">
              <a href="https://www.instagram.com/velocity.paintpanel/" target="_blank" rel="noopener noreferrer" data-testid="footer-ig-paint"
                className="flex items-center justify-center w-10 h-10 border border-white/10 text-zinc-500 hover:text-white hover:border-white/30 transition-colors">
                <Instagram size={16} />
              </a>
              <a href="https://www.instagram.com/velocitycoatingsau/" target="_blank" rel="noopener noreferrer" data-testid="footer-ig-coatings"
                className="flex items-center justify-center w-10 h-10 border border-white/10 text-zinc-500 hover:text-white hover:border-white/30 transition-colors">
                <Instagram size={16} />
              </a>
              <a href="https://www.facebook.com/profile.php?id=61587210241709" target="_blank" rel="noopener noreferrer" data-testid="footer-fb"
                className="flex items-center justify-center w-10 h-10 border border-white/10 text-zinc-500 hover:text-white hover:border-white/30 transition-colors">
                <Facebook size={16} />
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-heading text-sm tracking-[0.3em] uppercase text-zinc-400 mb-6">Location</h4>
            <div className="flex items-start gap-2 mb-4">
              <MapPin size={16} className="text-[#3b82f6] mt-0.5 shrink-0" />
              <p className="text-zinc-500 font-body text-sm leading-relaxed">
                101 Norman St<br />Woolloongabba, QLD 4102<br />Brisbane, Australia
              </p>
            </div>
            <p className="text-zinc-600 font-body text-xs mb-4">9am to 5pm / 7 Days / By Appointment Only</p>
            <div className="border border-white/10 overflow-hidden" style={{ filter: "invert(90%) hue-rotate(180deg) brightness(0.9) contrast(1.1)" }}>
              <iframe
                title="Velocity Paint and Panel Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3540.1!2d153.034!3d-27.489!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6b915a0b8cfd44ab%3A0x0!2s101+Norman+St%2C+Woolloongabba+QLD+4102!5e0!3m2!1sen!2sau!4v1700000000000!5m2!1sen!2sau"
                width="100%"
                height="150"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="footer-map"
              />
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-zinc-700 font-body tracking-wide">
            &copy; {new Date().getFullYear()} Velocity Paint and Panel. All rights reserved.
          </p>
          <button onClick={scrollToTop} data-testid="footer-scroll-top"
            className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-zinc-600 hover:text-white transition-colors cursor-pointer bg-transparent border-none">
            Back to top
            <ArrowUp size={14} />
          </button>
        </div>
      </div>
    </footer>
  );
};
